// Derived, memoizable view of one location for the guide: lookups, the
// menus/departments in a stable order, every dial route, and headline stats.
// Built once per flow version (see PhoneMenuGuide's useMemo keyed on
// flow.id + updatedAt — the 30 s poll hands us fresh object identities).

import type { CallFlow, CallFlowNode, AutoAttendantNode, QueueFlowNode } from '../../api/client';
import { enumeratePaths, guideStats, toIana, type DialPath } from './lib/callFlowStatus';
import type { Lang } from './i18n';

export interface GuideModel {
  flow: CallFlow;
  byId: Map<string, CallFlowNode>;
  /** Menus in walking order from the main number, then any others. */
  menus: AutoAttendantNode[];
  /** Menu ids that are only reached outside hours (after-hours flows). */
  afterHoursMenuIds: Set<string>;
  /** Departments (queues) sorted by their guide name. */
  queues: QueueFlowNode[];
  pathsByTarget: Map<string, DialPath[]>;
  iana: string | null;
  rootId: string;
  stats: { menus: number; departments: number; agents: number; groups: number };
  queuesByAgent: Map<string, QueueFlowNode[]>;
  agentIds: string[];
}

/** Per-language display name → Teams label. */
export function nodeName(node: CallFlowNode | undefined, lang: Lang): string {
  if (!node) return '';
  const d = node.displayName;
  return (lang === 'nl' ? d?.nl || d?.en : d?.en || d?.nl) || node.label;
}

export function flowTitle(flow: CallFlow, lang: Lang): string {
  const d = flow.displayName;
  return (lang === 'nl' ? d?.nl || d?.en : d?.en || d?.nl) || flow.locationName;
}

export function buildGuideModel(flow: CallFlow): GuideModel {
  const byId = new Map(flow.nodes.map(n => [n.id, n]));
  const pathsByTarget = enumeratePaths(flow);

  // Menus in the order a caller can reach them (breadth-first by route length).
  const reachable = [...pathsByTarget.entries()]
    .filter(([id]) => byId.get(id)?.type === 'autoAttendant')
    .sort((a, b) => (a[1][0]?.steps.length ?? 99) - (b[1][0]?.steps.length ?? 99) || a[0].localeCompare(b[0]))
    .map(([id]) => byId.get(id) as AutoAttendantNode);
  const seen = new Set(reachable.map(m => m.id));
  const afterHoursMenuIds = new Set<string>();
  const others: AutoAttendantNode[] = [];
  for (const n of flow.nodes) {
    if (n.type !== 'autoAttendant' || seen.has(n.id)) continue;
    others.push(n);
    afterHoursMenuIds.add(n.id);
  }
  const menus = [...reachable, ...others];

  const queues = (flow.nodes.filter(n => n.type === 'queue') as QueueFlowNode[])
    .slice()
    .sort((a, b) => nodeName(a, 'en').localeCompare(nodeName(b, 'en')));

  const queuesByAgent = new Map<string, QueueFlowNode[]>();
  for (const q of queues) {
    for (const a of q.agents || []) {
      const id = a.id.toLowerCase();
      const list = queuesByAgent.get(id) || [];
      list.push(q);
      queuesByAgent.set(id, list);
    }
  }

  return {
    flow, byId, menus, afterHoursMenuIds, queues, pathsByTarget,
    iana: toIana(flow.timezone),
    rootId: flow.openTargetId,
    stats: guideStats(flow),
    queuesByAgent,
    agentIds: [...queuesByAgent.keys()],
  };
}

export function shortestPath(model: GuideModel, targetId: string): DialPath | undefined {
  return model.pathsByTarget.get(targetId)?.[0];
}

/** "1 Nederlands › 1 Klantenservice" — the keys a caller presses. */
export function pathText(model: GuideModel, path: DialPath, lang: Lang): string {
  if (!path.steps.length) return nodeName(model.byId.get(model.rootId), lang);
  return path.steps.map(s => `${s.key} ${nodeName(model.byId.get(s.toId), lang)}`).join(' › ');
}

export function isQueue(n: CallFlowNode | undefined): n is QueueFlowNode {
  return !!n && n.type === 'queue';
}
